import type { ProfileModel } from "../types/models";
import type { SocialProviderAccount } from "../hooks/useSocialProviderAccounts";
import { hasActiveConsumerSubscription } from "./consumerSubscription";
import type { PlanType } from "../const/planConst";
import { getPaymentRequiredDialog } from "./pricingLimitDialog";
import { checkPublicationCreation } from "../components/publicationFlow/schedulingBatch";

/** A missing PB-only grant can need payment even without an expiry marker. */
export function needsPostBridgeRecoveryUpgrade(
  profile: Pick<ProfileModel, "plan" | "subscriptionStatus"> | null | undefined,
  accounts: readonly Pick<SocialProviderAccount, "needsReconnect" | "publicOnly" | "activationExpired">[] | undefined,
): boolean {
  return Boolean(profile && !hasActiveConsumerSubscription(profile) && accounts?.some(
    (account) => account.needsReconnect && (account.publicOnly || account.activationExpired),
  ));
}

/** A delayed policy response must not reopen a dialog after its owner leaves. */
export async function openAccountRecoveryUpgrade({
  accessToken, profile, isCurrent, refetchAccounts, open,
}: {
  accessToken: string;
  profile?: ProfileModel | null;
  isCurrent: () => boolean;
  refetchAccounts: () => Promise<unknown>;
  open: (dialog: ReturnType<typeof getPaymentRequiredDialog>) => void;
}) {
  const refusal = await checkPublicationCreation(accessToken);
  if (!isCurrent()) return;
  if (!refusal) {
    await refetchAccounts();
    return;
  }
  open(getPaymentRequiredDialog({
    ...refusal, requiredPlan: refusal.requiredPlan as PlanType | undefined,
    profile, unpaidVariant: "default", action: "publish",
  }));
}
